import React, { useState, useMemo, useEffect } from "react";
import { SubscriptionPlans } from "../plans";
import { fetchProducts } from "../plans/api/product-api";

/**
 * SubscriptionContent - Subscription page content component
 * 탭 상태 관리와 요금제 선택 로직 포함
 */
export const SubscriptionContent: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<string[]>([]);

  // API에서 모든 제품을 가져와서 unique category 추출
  useEffect(() => {
    fetchProducts()
      .then((products) => {
        const uniqueCategories = Array.from(
          new Set(products.map((p) => p.category))
        );
        setCategories(uniqueCategories);
      })
      .catch((error) => {
        console.error("Failed to fetch products:", error);
        setCategories([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const [selectedTab, setSelectedTab] = useState<string>("");

  // selectedTab 초기값 설정 (categories가 로드된 후)
  useEffect(() => {
    if (categories.length > 0 && !selectedTab) {
      setSelectedTab(categories[0]);
    }
  }, [categories, selectedTab]);

  if (loading) {
    return (
      <main className="flex-1">
        <section className="w-full bg-white pt-6 md:pt-8 lg:pt-10">
          <div className="max-w-5xl mx-auto px-4 lg:px-8 text-center">
            상품 정보를 불러오는 중...
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="flex-1">
      {/* Hero Section */}
      <section className="w-full bg-white pt-6 md:pt-8 lg:pt-10">
        <div className="max-w-5xl mx-auto px-4 lg:px-8">
          <div className="mb-8 md:mb-12">
            <p className="text-gray-400 text-base md:text-lg font-medium mb-4">
              구독 요금제 비교
            </p>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              초기 비용 부담 없이
              <br />
              합리적인 구독형 AI 학습 서비스
            </h1>
            <p className="text-base md:text-lg text-gray-700 leading-relaxed">
              GGP는 발달 전문가와 AI가 함께 설계한 유아 전용 디지털 학습
              솔루션으로, 아이의 성장 단계에 맞춘 콘텐츠를 정기 구독으로
              만나보세요.
            </p>
          </div>

          {/* Tab Navigation - Dynamic from API */}
          <div className="flex gap-4 mb-2">
            {categories
              .sort((a, b) => a.localeCompare(b, "ko-KR"))
              .map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedTab(category)}
                  className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
                    selectedTab === category
                      ? "bg-orange-500 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {category}
                </button>
              ))}
          </div>
        </div>
      </section>

      {/* Subscription Plans */}
      {selectedTab && <SubscriptionPlans selectedTab={selectedTab} />}
    </main>
  );
};
